import type { AnyObj, StringUnion } from '../../../typescript';
import { isServerSide, tryJsonParse } from '../../../utils';
import {
    formatV2RequestParams,
    isResearchV2BaseUrl,
    makeResearchV2ApiHeaders,
    snakeToCamelCaseDeep,
} from '../../config/research/v2';
import { BaseUrl } from '../base-urls';
import { HttpMethod } from '../http-method';
import { UserAgent } from '../user-agent';

import { type FetcherOptions, type RequestOptions } from './fetcher.model';
import { makeWordPressApiHeaders } from './utils/make-wordpress-api-headers';

/**
 * A custom class for `fetch` errors which extends `Error`.
 * The main advantage is to be able to easily access the response `status`
 * and the response `body`.
 *
 * @example
 * ```ts
 * try {
 *  callFetcher(...)
 * } catch (err) {
 *  const { status, errors, field, message, response } = err as FetcherError;
 *
 *  console.log(status, message)
 *  // log the original response body
 *  console.log(response)
 *  // log when there is a single error
 *  field && console.log(field)
 *  // or log when there are multiple errors
 *  errors && console.table(errors);
 *
 * }
 * ```
 * */
class FetcherError extends Error {
    /** Original response body from a fetch. */
    response: AnyObj | string | undefined;

    /** Status code returned from a fetch. */
    status: Response['status'];

    /** Success flag conveying whether a fetch failed or not. Will always be `false`. */
    success: false;

    /** Possible message for dev */
    devMessage?: string;

    /** Possible error message from server */
    error?: string;

    /** The error code attached to the error */
    errorCode?: number;

    /** Possibly message to display to user */
    userMessage?: string;

    more?: any;

    constructor(status: Response['status'], response?: AnyObj | string) {
        super();
        this.response = response;
        this.status = status;
        this.success = false;
        this.message =
            (response as AnyObj)?.data?.error ??
            (response as AnyObj)?._meta?.error ??
            'An unexpected error occured. Please try again later.';

        if (response) {
            if (typeof response === 'string') {
                this.message = response;
            } else {
                /** dynamically assign available fields: `message`, `errors`, field`
                 * and any other unknown KVP's in a given response */
                Object.assign(this, response);
            }
        }
    }
}

/**
 *
 * Gracefully handle  api errors ***AND*** network errors
 * @see https://tanstack.com/query/latest/docs/react/guides/query-functions#usage-with-fetch-and-other-clients-that-do-not-throw-by-default
 *
 * solution partially inspired by the following posts
 * @link https://stackoverflow.com/a/62774613
 * @link https://kentcdodds.com/blog/replace-axios-with-a-simple-custom-fetch-wrapper
 */
const parseFetchResponse = async <Res>(
    res: Response,
    useResearchApiV2Mapper?: boolean,
): Promise<FetcherError | Res> => {
    if (res?.ok) {
        /** 204 No Content */
        if (res.status === 204) {
            return {} as Res;
        }

        const json = await res.json();

        /** sometimes an API responds with a valid status, but there is an error */
        if (json?.errorCode && json?.error) {
            console.warn('Possible error in fetcher', json);
        }

        if (useResearchApiV2Mapper) {
            /** @todo throw errors for v2 api */
            const formattedResponse = snakeToCamelCaseDeep(json) as object;
            return formattedResponse as unknown as Res;
        }

        if (json?._meta?.success === false) {
            const jsonError = json.data?.message ?? json._meta?.message ?? 'Unknown API error';
            if (jsonError === 'Session error.') {
                console.warn('Session error detected.', json);
            } else {
                console.warn(`Possible error fetching from API: ${jsonError}`);
            }
        }

        return json as unknown as Res;
    }

    const textResponse = await res.text();
    const { parsedJson } = tryJsonParse(textResponse);

    return Promise.reject(new FetcherError(res?.status, parsedJson || textResponse));
};

type GetFetchOptionsOpts = {
    method: HttpMethod;
    baseUrl?: StringUnion<BaseUrl>;
    url: string;
} & Pick<FetcherOptions<any, any>, 'headers' | 'sessionToken' | 'body' | 'serverSideUserAgent'>;

const prepareRequestBody = (body: any) => {
    if (body instanceof FormData) {
        return body;
    } else if (typeof body === 'object') {
        return JSON.stringify(body);
    }
    return body;
};

const getFetchOptions = ({
    method,
    body,
    serverSideUserAgent,
    sessionToken,
    headers,
    baseUrl,
    url,
}: GetFetchOptionsOpts): RequestOptions => {
    const internalOptions: RequestOptions = {
        mode: 'cors',
        method: method || HttpMethod.Get,
        credentials: 'include',
        referrerPolicy: 'no-referrer',
    };

    /**
     * 1) Let's determine options
     * */
    if (body) {
        let modifiedBody = body;
        if (isResearchV2BaseUrl(baseUrl)) {
            modifiedBody = formatV2RequestParams(body);
        }
        internalOptions.body = prepareRequestBody(modifiedBody);
    }

    const isPoster = method === HttpMethod.Patch || method === HttpMethod.Post || method === HttpMethod.Put;
    if (isPoster) {
        internalOptions.cache = 'no-cache';
        internalOptions.redirect = 'follow';
    }

    const clientHeaders: RequestInit['headers'] = {};

    if (!isServerSide()) {
        const researchSessionToken = window.sessionStorage.getItem('sessionToken');
        if (researchSessionToken) {
            clientHeaders['X-Research-Session-Token'] = researchSessionToken;
        }
    }

    /**
     * 2) Let's determine headers
     * */
    const internalHeaders: RequestInit['headers'] = {
        ...clientHeaders,
        ...(sessionToken && { 'X-Research-Session-Token': sessionToken }),
    };

    /** 👇 for `FormData` body, content type is automagically inferred */
    if (internalOptions?.body instanceof FormData) {
        /** 👇 so let's set `Accept` header to: */
        internalHeaders.Accept = 'application/json';
    } else {
        /** 👇 or by default, let's set `Content-Type` to: */
        internalHeaders['Content-Type'] = 'application/json';
    }

    if (isServerSide()) {
        /** for cloudflare / DDOS */
        internalHeaders['User-Agent'] =
            serverSideUserAgent ?? process.env.NEXT_PUBLIC_FETCHER_USER_AGENT ?? UserAgent.Research;
    }
    return {
        ...internalOptions,
        headers: {
            ...makeResearchV2ApiHeaders(url, baseUrl),
            ...makeWordPressApiHeaders(baseUrl),
            ...internalHeaders,
            ...(headers || {}),
        },
    };
};

export { getFetchOptions, parseFetchResponse };
